import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles,
  Coins,
  Loader2,
  Copy,
  Share2,
  Trophy,
  UserPlus,
  ArrowRight,
  ChevronLeft,
  Calendar,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AmbassadorBadge from "@/components/AmbassadorBadge";

/**
 * AmbassadorDashboard — private control room for approved Panda
 * Ambassadors at `/ambassador/dashboard`.
 *
 * Guards:
 *  - Anonymous → redirected to `/login?next=/ambassador/dashboard`
 *  - Signed-in but not approved → redirected to `/ambassadors/apply`
 *
 * Renders:
 *  - Tier + next-drop countdown + total earnings
 *  - Unique referral URL with copy + native share
 *  - Recruit table (avatar / handle / join date), sortable by newest
 *  - Monthly + all-time headline counts
 *  - Tier progress bar toward the next threshold
 */
const NEXT_TIER_TARGETS = { bronze: 20, silver: 50, gold: null };

export default function AmbassadorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const authChecking = user === null;
  const isLoggedIn = !!user && user !== false;

  const [me, setMe] = useState(null);
  const [recruits, setRecruits] = useState({ count: 0, this_month: 0, rows: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authChecking) return;
    if (!isLoggedIn) {
      navigate(`/login?next=${encodeURIComponent("/ambassador/dashboard")}`);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([api.get("/ambassador/me"), api.get("/ambassador/recruits")])
      .then(([meRes, recRes]) => {
        if (cancelled) return;
        setMe(meRes.data);
        setRecruits(recRes.data);
        // Non-approved users get redirected to the marketing page — no dashboard for them.
        if (meRes.data?.status !== "approved") {
          navigate("/ambassadors/apply", { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) navigate("/ambassadors/apply", { replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authChecking, isLoggedIn, navigate]);

  const referralUrl = useMemo(() => {
    const handle = me?.handle || user?.handle;
    if (!handle) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/ref/${handle}`;
  }, [me, user]);

  const copy = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied.");
    } catch {
      toast.error("Couldn't access clipboard — copy manually.");
    }
  };

  const share = async () => {
    if (!referralUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on My Panda X",
          text: "Come join me on My Panda X — grab your +50 coin welcome bonus.",
          url: referralUrl,
        });
      } catch {
        // User dismissed the share sheet — no-op.
      }
    } else {
      copy();
    }
  };

  if (loading || authChecking) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 size={16} className="animate-spin" /> Loading your dashboard…
        </div>
      </div>
    );
  }
  if (!me || me.status !== "approved") return null; // navigate() already fired

  const tier = me.tier || "bronze";
  const referrals = me.referrals_count || 0;
  const nextTarget = NEXT_TIER_TARGETS[tier];
  const progressPct =
    nextTarget != null ? Math.min(100, Math.round((referrals / nextTarget) * 100)) : 100;
  const nextDrop = me.next_drop_at ? new Date(me.next_drop_at) : null;
  const daysUntilDrop = nextDrop
    ? Math.max(0, Math.ceil((nextDrop.getTime() - Date.now()) / (24 * 3600 * 1000)))
    : null;

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="ambassador-dashboard-page">
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <Link
          to="/ambassadors/apply"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white mb-4"
          data-testid="ambassador-dashboard-back"
        >
          <ChevronLeft size={14} /> Program details
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pb-6 border-b border-white/10">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-amber-400 mb-2">
              <Sparkles size={12} /> Ambassador dashboard
            </div>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight">
              Welcome back,{" "}
              <span className="text-amber-300">{me.display_name}</span>.
            </h1>
          </div>
          <AmbassadorBadge status="approved" tier={tier} size="lg" linkTo={null} />
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <StatCard
            label="Total invites"
            value={referrals.toLocaleString()}
            icon={UserPlus}
            testid="ambassador-dashboard-stat-total"
          />
          <StatCard
            label="This month"
            value={recruits.this_month.toLocaleString()}
            icon={Calendar}
            testid="ambassador-dashboard-stat-month"
          />
          <StatCard
            label="Tier"
            value={<span className="capitalize">{tier}</span>}
            icon={Trophy}
            testid="ambassador-dashboard-stat-tier"
          />
          <StatCard
            label="Next drop"
            value={daysUntilDrop != null ? `${daysUntilDrop}d` : "—"}
            icon={Coins}
            testid="ambassador-dashboard-stat-drop"
            hint={`${(me.monthly_drop_coins || 500).toLocaleString()} coins`}
          />
        </div>

        {/* Tier progress */}
        <section className="mt-8 bg-zinc-950/60 border border-white/10 rounded-md p-4">
          <div className="flex items-center justify-between text-xs uppercase tracking-widest text-zinc-500 mb-2">
            <span>Tier progress</span>
            <span data-testid="ambassador-dashboard-tier-progress-label">
              {nextTarget != null
                ? `${referrals} / ${nextTarget} to ${tier === "bronze" ? "Silver" : "Gold"}`
                : "Maxed out at Gold — legend."}
            </span>
          </div>
          <div className="h-2 bg-black/60 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 transition-all"
              style={{ width: `${progressPct}%` }}
              data-testid="ambassador-dashboard-tier-progress-bar"
            />
          </div>
        </section>

        {/* Referral link */}
        <section className="mt-8" data-testid="ambassador-dashboard-referral-section">
          <div className="text-xs uppercase tracking-[0.35em] text-zinc-500 mb-3">
            Your referral link
          </div>
          <div className="bg-gradient-to-r from-rose-500/10 to-amber-500/10 border border-rose-500/30 rounded-md p-4">
            <div className="flex items-center gap-2 bg-black/60 border border-white/10 rounded px-3 py-2.5 text-xs text-zinc-200 font-mono">
              <span
                className="truncate flex-1"
                data-testid="ambassador-dashboard-referral-url"
              >
                {referralUrl}
              </span>
              <button
                type="button"
                onClick={copy}
                data-testid="ambassador-dashboard-copy-btn"
                className="text-rose-300 hover:text-rose-200 uppercase tracking-widest text-[10px] flex items-center gap-1"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                type="button"
                onClick={share}
                data-testid="ambassador-dashboard-share-btn"
                className="bg-gradient-to-r from-rose-600 to-rose-500 hover:brightness-110 text-white text-xs font-semibold rounded-full px-4 py-2 flex items-center gap-1.5"
              >
                <Share2 size={12} /> Share
              </button>
              <span className="text-[11px] text-zinc-500">
                Every signup credits <b className="text-white">+100 coins</b> to your wallet ·
                friends land on a personal welcome page.
              </span>
            </div>
          </div>
        </section>

        {/* Recruits table */}
        <section className="mt-10" data-testid="ambassador-dashboard-recruits-section">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.35em] text-zinc-500">
              Your recruits
            </div>
            <div className="text-[11px] text-zinc-500">
              {recruits.count} total · {recruits.this_month} this month
            </div>
          </div>
          {recruits.rows.length === 0 ? (
            <div className="bg-zinc-950/60 border border-dashed border-white/10 rounded-md p-8 text-center">
              <p className="text-sm text-zinc-400">
                No signups yet from your link. Share it in a bio or DM to get the first invite.
              </p>
              <button
                type="button"
                onClick={share}
                data-testid="ambassador-dashboard-empty-share"
                className="mt-4 inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-full px-4 py-2"
              >
                <Share2 size={12} /> Share your link
              </button>
            </div>
          ) : (
            <div className="border border-white/10 rounded-md overflow-hidden">
              <ul className="divide-y divide-white/5" data-testid="ambassador-dashboard-recruits-list">
                {recruits.rows.map((r, i) => (
                  <RecruitRow key={r.id} recruit={r} idx={i + 1} />
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Leaderboard link */}
        <section className="mt-10 pt-6 border-t border-white/10 text-center">
          <Link
            to="/ambassadors"
            className="inline-flex items-center gap-2 text-sm text-amber-300 hover:text-amber-200"
            data-testid="ambassador-dashboard-view-leaderboard"
          >
            <Trophy size={14} /> See where you stand on the leaderboard <ArrowRight size={12} />
          </Link>
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, testid, hint }) {
  return (
    <div
      className="bg-zinc-950/60 border border-white/10 rounded-md p-3"
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
        <Icon size={10} /> {label}
      </div>
      <div className="text-2xl text-white font-display leading-tight mt-1">{value}</div>
      {hint && <div className="text-[10px] text-zinc-600 mt-0.5">{hint}</div>}
    </div>
  );
}

function RecruitRow({ recruit, idx }) {
  const joined = recruit.created_at ? new Date(recruit.created_at) : null;
  return (
    <li
      className="flex items-center gap-3 px-4 py-3 bg-zinc-950/40 hover:bg-zinc-950/70 transition-colors"
      data-testid={`ambassador-dashboard-recruit-${recruit.id}`}
    >
      <div className="w-6 text-center text-xs text-zinc-500 font-display">
        #{idx}
      </div>
      <Avatar className="w-9 h-9 border border-white/10">
        <AvatarImage src={recruit.avatar_url} />
        <AvatarFallback className="bg-rose-600 text-white text-xs">
          {recruit.display_name?.[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <Link
        to={`/u/${recruit.id}`}
        className="flex-1 min-w-0 hover:text-rose-300"
      >
        <div className="text-sm text-white truncate">
          {recruit.display_name || recruit.handle}
        </div>
        <div className="text-[11px] text-zinc-500 truncate">{recruit.display_name}</div>
      </Link>
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Joined</div>
        <div className="text-xs text-zinc-300">
          {joined ? joined.toLocaleDateString() : "—"}
        </div>
      </div>
    </li>
  );
}
